import type { User } from '@/lib/auth-store';
import {
  isSuperPlatformTab,
  resolveAdminPermissionKey,
  SUPER_PLATFORM_DEDICATED_PATHS,
} from '@/config/admin-permission-map';
import type { PermissionKey, PermissionRequirement } from '@/lib/permissions';
import { hasPermission } from '@/lib/permissions';
import { getEffectiveRole, normalizeRole } from '@/lib/rbac';

export function hasSuperPlatformAccess(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.super_platform_access === true) return true;
  if (user.master_admin === true) return true;
  const rank = user.hierarchy_rank ?? 0;
  return rank >= 1000;
}

/** Mirrors backend DavsPermissions::userCan — admin / super_admin retain full DAVS access. */
export function hasPlatformDavsAccess(user: User | null | undefined): boolean {
  if (!user) return false;
  if (hasSuperPlatformAccess(user)) return true;
  const effective = getEffectiveRole(user);
  if (effective === 'admin' || effective === 'super_admin') return true;
  return (user.roles ?? []).some((role) => {
    const normalized = normalizeRole(role);
    return normalized === 'admin' || normalized === 'super_admin';
  });
}

function isDavsPermission(permission?: PermissionRequirement): boolean {
  if (!permission) return false;
  if (typeof permission === 'string') return permission.startsWith('davs.');
  if (Array.isArray(permission)) return permission.some((key) => key.startsWith('davs.'));
  return [...(permission.anyOf ?? []), ...(permission.allOf ?? [])].some((key) =>
    key.startsWith('davs.'),
  );
}

export function hasNavPermission(user: User | null | undefined, requirement?: PermissionRequirement): boolean {
  if (!requirement) return true;
  if (typeof requirement === 'string') {
    return hasPermission(user, resolveAdminPermissionKey(requirement) as PermissionRequirement);
  }
  if (Array.isArray(requirement)) {
    return requirement.every((key) => hasPermission(user, resolveAdminPermissionKey(key) as PermissionRequirement));
  }
  const anyOf = requirement.anyOf?.map((k) => resolveAdminPermissionKey(k) as PermissionKey) ?? [];
  const allOf = requirement.allOf?.map((k) => resolveAdminPermissionKey(k) as PermissionKey) ?? [];
  return hasPermission(user, { anyOf, allOf });
}

export function canAccessAdminNavItem(
  user: User | null | undefined,
  tab: string,
  permission?: PermissionRequirement,
  superPlatformOnly = false,
): boolean {
  if (superPlatformOnly || isSuperPlatformTab(tab)) {
    if (!hasSuperPlatformAccess(user)) return false;
  }
  // Consent Records: Super Platform always; Compliance via view_legal_consents.
  if (tab === 'consent-records') {
    if (hasSuperPlatformAccess(user)) return true;
    return hasNavPermission(user, 'view_legal_consents');
  }
  // Platform admin / super_admin retain DAVS nav (same as API); limited admins still need davs.*.
  if (permission && isDavsPermission(permission) && hasPlatformDavsAccess(user)) {
    return true;
  }
  return hasNavPermission(user, permission);
}

export function isSuperPlatformDedicatedPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const normalized = pathname.replace(/\/$/, '') || '/';
  return SUPER_PLATFORM_DEDICATED_PATHS.some(
    (path) => normalized === path || normalized.startsWith(`${path}/`),
  );
}

export function canAccessSuperPlatformPath(user: User | null | undefined, pathname: string | null | undefined): boolean {
  if (!isSuperPlatformDedicatedPath(pathname)) return true;
  return hasSuperPlatformAccess(user);
}
